import { defineComponent as _defineComponent } from 'vue'
import { Ref, computed, inject, ref, onBeforeMount, watch } from 'vue';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  ButtonPrimary,
  Heading,
  InputText,
  Link,
  FilterList,
  ButtonIconContextual
} from '@warrenbrasil/nebraska-web';
import { debounce } from 'lodash-es';
import { ValidationError } from 'yup';
import { useRoute } from 'vue-router/composables';
import {
  YupError,
  InputStatusType,
  ProspectSignUp,
  SignUpFormSteps,
  Advisor,
  SignUpError,
  IErrorModalContent,
  ECoexistenceStatus
} from '@/modules/onboarding/signup/types';
import { initialFormStepSchema } from '@/modules/onboarding/signup/components/InitialFormStep/validate';
import { emailSuggester } from '@/modules/onboarding/signup/helpers/email-suggester';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { notify } from '@/modules/common/helpers/notification';
import { FadeTransition } from '@/modules/common/components/FadeTransition';
import {
  getProspectStatus,
  prospectSignUp
} from '@/modules/onboarding/signup/services/api';
import {
  trackFillCpf,
  trackFillEmail,
  trackFillName,
  trackFillPhone,
  trackSelectAdvisorExistence,
  trackSendForm
} from '@/modules/onboarding/signup/helpers/track-events';
import { ErrorModal } from './components/ErrorModal';
import { AdvisorStep } from './components/AdvisorStep';
import { AutoCompleteSelector } from './components/AutoCompleteSelector';

import {
  IRecaptcha,
  Recaptcha,
  RecaptchaActions
} from '@/modules/authentication/components/Recaptcha';


export default /*#__PURE__*/_defineComponent({
  __name: 'InitialFormStep',
  setup(__props) {

const form = ref<ProspectSignUp>({
  name: '',
  email: '',
  cpf: '',
  phone: '',
  hasAdvisor: ''
});

const timer = ref<NodeJS.Timer>();
const errors = ref<YupError[]>([]);
const isLoading = ref<boolean>(false);
const isErrorModalOpen = ref<boolean>(false);
const suggestedEmail = ref<string>('');
const recaptcha = ref<InstanceType<typeof Recaptcha> | null>(null);
const lastRecaptchaAction = ref<RecaptchaActions>();
const recaptchaToken = ref<string>('');
const showAdvisorDisclaimer = ref(false);

const isAdvisorModalOpen = inject('isAdvisorModalOpen') as Ref<boolean>;
const updateIsAdvisorModalOpen = inject('updateIsAdvisorModalOpen') as (
  value: boolean
) => void;
const changeStep = inject('changeStep', (step: SignUpFormSteps) => ({ step }));
const userHaveAdvisor = inject('userHaveAdvisor') as Ref<boolean>;
const advisor = inject('advisor') as Ref<Advisor>;
const advisorLoading = inject('advisorLoading') as Ref<boolean>;
const updateFirstName = inject('updateFirstName', (value: string) => ({
  value
}));
const updateEmail = inject('updateEmail', (value: string) => ({ value }));
const partnerThemeLoading = inject('partnerThemeLoading') as Ref<boolean>;
const isMobile = inject('isMobile') as Ref<boolean>;
const route = useRoute();
const errorModalContent = ref<IErrorModalContent>({
  title: '',
  description: '',
  showButtonToLogin: false
});

const advisorFormOptions = computed(() => {
  return [
    { label: 'Sim', value: 'true', disabled: advisorLoading.value },
    { label: 'Não', value: 'false', disabled: advisorLoading.value }
  ];
});

const showAdvisorField = computed(
  () => !userHaveAdvisor.value && !partnerThemeLoading?.value
);

const formFulfilled = computed(() => {
  const filteredFormValues = Object.entries(form.value)
    .filter(([key]) => key !== 'token')
    .map(([, value]) => value);

  return filteredFormValues.some(value => !value) || errors.value.length > 0;
});

function blurName() {
  validateField({ key: 'name' });
  trackFillName();
}

function blurEmail() {
  validateField({ key: 'email', useDebounce: true });
  trackFillEmail();
}

function blurCpf() {
  validateField({ key: 'cpf' });
  trackFillCpf();
}

function blurPhone() {
  validateField({ key: 'phone' });
  trackFillPhone();
}

function updateEmailByAutocomplete(email: string) {
  form.value.email = email;
  validateField({ key: 'email', useDebounce: true });
}

function updateAdvisorEmail(email: string) {
  form.value.advisorEmail = email;

  checkConditions();
}

function clickSuggestedEmail() {
  form.value.email = suggestedEmail.value;
  suggestedEmail.value = '';
  validateField({ key: 'email', useDebounce: true });
}

function openAdvisorDisclaimer() {
  showAdvisorDisclaimer.value = true;

  timer.value = setTimeout(() => {
    showAdvisorDisclaimer.value = false;
  }, 7000);
}

function closeAdvisorDisclaimer() {
  showAdvisorDisclaimer.value = false;

  if (timer.value) clearTimeout(timer.value);
}

async function validateField({
  key,
  useDebounce = false
}: {
  key: string;
  useDebounce?: boolean;
}) {
  const validationCode = async () => {
    try {
      await initialFormStepSchema.validateAt(key, form.value);
      removeError(key);
    } catch (err) {
      if (err instanceof ValidationError) {
        errors.value.push({
          key,
          value: err.errors[0]
        });
      }
    }
  };

  if (useDebounce) {
    const debouncedValidation = debounce(validationCode, 150);
    debouncedValidation();
  } else {
    await validationCode();
  }
}
function getInputStatus(key: string) {
  return errors.value.find(error => error.key === key)
    ? InputStatusType.Error
    : InputStatusType.Default;
}

function getErrorMessage(key: string) {
  return errors.value.find(error => error.key === key)?.value || '';
}

function removeError(key: string) {
  errors.value = errors.value.filter(error => error.key !== key);
}

async function handleProspectStatus() {
  try {
    const response = await getProspectStatus(
      form.value.email,
      recaptchaToken.value
    );
    if (response.status === ECoexistenceStatus.Prospect) {
      updateEmail(form.value.email);
      updateFirstName(form.value.name.split(' ')[0]);
      changeStep(SignUpFormSteps.Password);
      return true;
    }
  } catch (err) {
    treatBffError(err as SignUpError);
    return true;
  }
}

function handleAdvisorCheckbox() {
  if (form.value.hasAdvisor === 'true' && !form.value.advisorEmail) {
    trackSelectAdvisorExistence();
    updateIsAdvisorModalOpen(true);
    return true;
  }
  return false;
}

async function handleErrors() {
  for (const key of Object.keys(form.value)) {
    await validateField({ key });
  }
  if (errors.value.length > 0) return true;

  return false;
}

function handleEmailSuggestion() {
  const emailSuggestion = emailSuggester(form.value.email);

  const shouldSuggestEmail =
    emailSuggestion.suggestion &&
    !emailSuggestion.isKnownDomain &&
    !suggestedEmail.value;

  suggestedEmail.value = shouldSuggestEmail
    ? emailSuggestion.suggestion ?? ''
    : '';

  return shouldSuggestEmail;
}

async function handleRecaptcha(RecaptchaAction: RecaptchaActions) {
  const recaptchaEl = recaptcha.value as unknown as IRecaptcha;
  isLoading.value = true;

  try {
    lastRecaptchaAction.value = RecaptchaAction;
    await recaptchaEl.onChallenge(RecaptchaAction);
  } catch {
    isLoading.value = false;
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao continuar, verifique sua internet e tente novamente.',
      type: NotificationTypes.error
    });
  }
}

async function onRecaptchaToken(token: string) {
  recaptchaToken.value = token;
  if (
    recaptchaToken.value &&
    lastRecaptchaAction.value === RecaptchaActions.PROSPECT_STATUS
  ) {
    await checkConditions();
  } else if (
    recaptchaToken.value &&
    lastRecaptchaAction.value === RecaptchaActions.SIGN_UP
  ) {
    await submit();
  }
}

async function checkConditions() {
  isLoading.value = true;
  const conditions = [
    handleProspectStatus,
    handleAdvisorCheckbox,
    handleErrors,
    handleEmailSuggestion
  ];

  for (const condition of conditions) {
    const shouldStopSubmit = await condition();
    if (shouldStopSubmit) {
      isLoading.value = false;
      return;
    }
  }

  await handleRecaptcha(RecaptchaActions.SIGN_UP);
}

async function submit() {
  trackSendForm();
  try {
    const filteredForm = { ...form.value };
    delete filteredForm.hasAdvisor;
    filteredForm.cpf = filteredForm.cpf.replace(/\D/g, '');
    filteredForm.phone = filteredForm.phone.replace(/\D/g, '');

    await prospectSignUp(filteredForm, recaptchaToken.value, route.query);
    updateEmail(form.value.email);
    updateFirstName(form.value.name.split(' ')[0]);
    changeStep(SignUpFormSteps.Password);
  } catch (err) {
    isAdvisorModalOpen.value = false;
    treatBffError(err as SignUpError);
  }
  isLoading.value = false;
}

function treatBffError(err: SignUpError) {
  if (err?.error?.locations) {
    errorModalContent.value.title = err.error.locations[0].message.formatted;
    errorModalContent.value.description =
      err.error.locations[1].message.formatted;
    errorModalContent.value.showButtonToLogin =
      err.error.key === 'email_exists';
  } else if (err?.error?.message) {
    errorModalContent.value.title = 'Erro ao criar conta';
    errorModalContent.value.description = err.error.message.formatted;
    errorModalContent.value.showButtonToLogin = false;
  } else {
    errorModalContent.value.title = 'Erro ao criar conta';
    errorModalContent.value.description =
      'Não foi possível realizar a operação, por favor tente novamente.';
    errorModalContent.value.showButtonToLogin = false;
  }
  isErrorModalOpen.value = true;
}

onBeforeMount(() => {
  const route = useRoute();

  Object.keys(form.value).forEach(key => {
    const safeKey = key as keyof ProspectSignUp;
    if (route.query[safeKey]) {
      form.value[safeKey] = route.query[safeKey] as string;
    }
  });

  if (!showAdvisorField.value) form.value.hasAdvisor = 'true';
});

watch(userHaveAdvisor, () => {
  userHaveAdvisor && (form.value.advisorEmail = advisor.value.email);
});

return { __sfc: true,form, timer, errors, isLoading, isErrorModalOpen, suggestedEmail, recaptcha, lastRecaptchaAction, recaptchaToken, showAdvisorDisclaimer, isAdvisorModalOpen, updateIsAdvisorModalOpen, changeStep, userHaveAdvisor, advisor, advisorLoading, updateFirstName, updateEmail, partnerThemeLoading, isMobile, route, errorModalContent, advisorFormOptions, showAdvisorField, formFulfilled, blurName, blurEmail, blurCpf, blurPhone, updateEmailByAutocomplete, updateAdvisorEmail, clickSuggestedEmail, openAdvisorDisclaimer, closeAdvisorDisclaimer, validateField, getInputStatus, getErrorMessage, removeError, handleProspectStatus, handleAdvisorCheckbox, handleErrors, handleEmailSuggestion, handleRecaptcha, onRecaptchaToken, checkConditions, submit, treatBffError, NebraskaColors, BaseText, ButtonPrimary, Heading, InputText, Link, FilterList, ButtonIconContextual, FadeTransition, ErrorModal, AdvisorStep, AutoCompleteSelector, Recaptcha, RecaptchaActions }
}

})